import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API } from '../api/client';
import { useApi } from '../api/useApi';

/** Render a multi-line string as one paragraph per line (blank lines skipped). */
function MultiLine({ text }: { text?: string }) {
  const lines = (text || '').split('\n');
  return (
    <>
      {lines.map((ln, i) => (ln.trim() ? <p key={i} style={{ margin: '0 0 6px' }}>{ln}</p> : null))}
    </>
  );
}

export default function NotesExport() {
  const { fiId } = useParams<{ fiId: string }>();
  const navigate = useNavigate();
  const { data: fetched, loading, error } = useApi<any>(() => API.fiNotes(fiId!), [fiId]);

  // Local mirror of the notes response so we can update it in place after a
  // (re)generate without a full refetch (the generate endpoint returns the same shape).
  const [notes, setNotes] = useState<any>(null);
  useEffect(() => { setNotes(fetched); }, [fetched]);

  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError(null);
    setExported(false);
    try {
      const res = await API.generateNotes(fiId!);
      setNotes(res);
    } catch (e: any) {
      setGenError(e?.message || 'Generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await API.exportNotes({ fiId });
      setExported(true);
    } catch (e: any) {
      setExportError(e?.message || 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !notes) return <div className="phil-error">Unable to load notes. {error}</div>;

  const fi = { id: fiId, name: notes.fiName };
  const payload = notes.notes;           // structured notes, or null if not generated yet
  const generated = notes.generated && payload;
  const generatedAt = notes.generatedAt ? new Date(notes.generatedAt).toLocaleString() : null;

  return (
    <>
      <div className="breadcrumb">
        <a onClick={() => navigate('/feedback-priorities')}>Feedback &amp; Priorities</a>
        <span className="bc-sep">›</span>
        <a onClick={() => navigate(`/fi/${fi.id}`)}>{fi.name}</a>
        <span className="bc-sep">›</span>
        <span>Notes</span>
      </div>

      <div className="page-header">
        <h1>{fi.name} — Priority Call Notes</h1>
        <p className="subtitle">
          {generatedAt ? `Generated ${generatedAt}` : 'Not generated yet'} · {notes.cycle} program year
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
        <button
          className={`btn btn-sm ${generated ? 'btn-outline' : 'btn-primary'}`}
          disabled={generating}
          onClick={handleGenerate}
        >
          {generating ? 'Generating…' : generated ? '↻ Regenerate notes' : '✨ Generate notes'}
        </button>
        <button
          className={`btn btn-sm ${exported ? 'btn-outline' : 'btn-primary'}`}
          disabled={!generated || generating || exporting}
          title={generated ? '' : 'Generate the notes first'}
          onClick={handleExport}
        >
          {exporting ? 'Downloading…' : exported ? '✓ Downloaded' : '📄 Export this FI (.docx)'}
        </button>
      </div>
      {genError && <div className="phil-error" style={{ marginBottom: 12 }}>{genError}</div>}
      {exportError && <div className="phil-error" style={{ marginBottom: 12 }}>{exportError}</div>}

      <div className="notes-doc">
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#9ca3af', marginBottom: 12 }}>
          {notes.cycle} Priority Call Notes · preview of the .docx export
        </div>

        {!generated ? (
          <div className="phil-empty" style={{ padding: '32px 0', textAlign: 'center', color: '#6b7280' }}>
            {generating
              ? 'Summarizing the call transcript…'
              : 'No notes generated yet. Click “Generate notes” to summarize the call transcript into the export format.'}
          </div>
        ) : (
          <>
            <h2>{payload.fiName} {payload.year}</h2>
            <p><strong>Date:</strong> {payload.date || '—'}</p>
            <p><strong>Attendees:</strong> {payload.attendees || '—'}</p>
            <p><strong>Feedback:</strong> {payload.feedback || '—'}</p>

            <h4>Action Items</h4>
            {payload.actionItems?.length
              ? <ul>{payload.actionItems.map((a: string, i: number) => <li key={i}>{a}</li>)}</ul>
              : <p>—</p>}

            <h4>Notes</h4>
            {payload.notes ? <MultiLine text={payload.notes} /> : <p>—</p>}

            <h4>Top 5 Priorities</h4>
            {payload.priorities?.length
              ? <ul>{payload.priorities.map((p: string, i: number) => <li key={i}>{p}</li>)}</ul>
              : <p>—</p>}

            <h4>Transcript</h4>
            <div
              style={{
                maxHeight: 320, overflowY: 'auto', whiteSpace: 'pre-wrap',
                border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 12px',
                background: '#f9fafb', fontSize: 13, lineHeight: 1.5,
              }}
            >
              {payload.transcript || '(no transcript captured)'}
            </div>
          </>
        )}
      </div>

      <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate(`/calls/${fi.id}/review`)}>← Call Review</button>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/feedback-priorities/scoring')}>H/M/L Scoring →</button>
        <button className="btn btn-outline btn-sm" onClick={() => navigate(`/calls/${fi.id}/thank-you`)}>Thank-You Email →</button>
      </div>
    </>
  );
}
